import axios from 'axios';
import { normalizeRegisteredBusiness } from '../utils/businessOnboarding';

// Use environment variable for API URL, fallback to localhost for development
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/** Ensure every business record has a stable business_id (GET /businesses often omits it). */
export function normalizeBusinessRecord(data, fallbackId) {
  return normalizeRegisteredBusiness(data, fallbackId);
}

// Create axios instance with increased timeout
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased from 30s to 60s for better reliability
  headers: {
    'Content-Type': 'application/json',
  },
});


const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add the missing handleResponse function
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }
  return {
    data: await response.json(),
    status: response.status
  };
};

// Update the test chat method
const testChat = (businessId, data) => {
  // Ensure conversation_id is null or marked as test
  const testData = {
    ...data,
    conversation_id: null,  // Force null to ensure it's not saved 
    is_test: true,  // Add explicit test flag
  };
  return apiClient.post(`/test-chat/${businessId}`, testData);
};

// Add Google Places API methods
const googlePlaces = {
  search: async (query, location = null) => {
    const response = await fetch(`${API_BASE_URL}/google-places/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        textQuery: query,  // Changed from 'query' to 'textQuery' to match backend
        pageSize: 10,
        locationBias: location ? {
          circle: {
            center: { latitude: location.lat, longitude: location.lng },
            radius: 50000
          }
        } : null
      })
    });
    return handleResponse(response);
  },

  getDetails: async (placeId) => {
    const response = await fetch(`${API_BASE_URL}/google-places/details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        place_id: placeId
      })
    });
    return handleResponse(response);
  },

  autocomplete: async (input, types = 'establishment') => {
    const params = new URLSearchParams({
      input,
      types,
      radius: '50000'
    });
    
    const response = await fetch(`${API_BASE_URL}/google-places/autocomplete?${params}`);
    return handleResponse(response);
  }
};

export const api = {
  // Business endpoints
  createBusiness: (data) => 
    apiClient.post('/businesses', data)
      .then(response => ({
        data: normalizeBusinessRecord(
          response.data,
          response.data.id || response.data.business_id,
        ),
      })),
  getBusiness: (businessId) => 
    apiClient.get(`/businesses/${businessId}`)
      .then(response => ({
        data: normalizeBusinessRecord(response.data, businessId),
      })),
  getBusinessByName: (name) => apiClient.get(`/businesses/by-name/${name}`),
  getBusinessByEmail: (email) => 
    apiClient.get(`/businesses/by-email/${encodeURIComponent(email)}`)
      .then(response => ({
        data: normalizeBusinessRecord(response.data),
      }))
      .catch(error => {
        if (error.response?.status === 404) {
          return { data: null };
        }
        throw error;
      }),
  updateBusiness: (businessId, data) => 
    apiClient.put(`/businesses/${businessId}`, {
      settings: {
        ...data.settings,
        updatedAt: new Date().toISOString()
      }
    }),


  deleteBusiness: (businessId) => 
    apiClient.delete(`/businesses/${businessId}`, {
      timeout: 60000, // 60 seconds timeout for comprehensive deletion
    })
    .then(response => {
      console.log('Business deletion completed successfully:', response.data);
      return response;
    })
    .catch(error => {
      console.error('Business deletion error:', error);
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Deletion error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        error.message = 'Deletion timeout - the process is taking longer than expected. Please check if deletion completed successfully.';
      }
      throw error;
    }),

  // Document endpoints
  uploadDocument: (businessId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/businesses/${businessId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listDocuments: (businessId) => apiClient.get(`/businesses/${businessId}/documents`),
  deleteDocument: (businessId, documentId) => 
    apiClient.delete(`/businesses/${businessId}/documents/${documentId}`),

  // Add new method for website processing with improved error handling
  processWebsite: (businessId, data) => 
    apiClient.post(`/businesses/${businessId}/documents/website`, data)
      .catch(error => {
        // Extract the specific error message if available
        if (error.response?.data?.detail) {
          console.error(`Website processing error: ${error.response.data.detail}`);
          error.message = error.response.data.detail;
        } else {
          console.error(`Website processing error: ${error.message}`);
        }
        throw error;
      }),

  // Add new method to check processing status
  waitForProcessing: async (businessId, documentId) => {
    const maxAttempts = 30; // Maximum number of attempts
    const delay = 2000; // 2 seconds between attempts

    for (let i = 0; i < maxAttempts; i++) {
      const response = await apiClient.get(`/businesses/${businessId}/documents/${documentId}/status`);
      if (response.data.status === 'indexed') {
        return response.data;
      } else if (response.data.status === 'failed') {
        throw new Error('Processing failed');
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    throw new Error('Processing timeout');
  },

  // Chat endpoints
  sendMessage: (businessId, data) => 
    apiClient.post(`/businesses/${businessId}/chat`, {
      message: data.message,
      business_id: data.business_id,
      conversation_id: data.conversation_id,
      whatsapp_enabled: data.whatsapp_enabled,
      whatsapp_number: data.whatsapp_number
    }),

  // Widget chat endpoint with proper credit handling
  sendWidgetMessage: (businessId, data) =>
    apiClient.post(`/widget/chat/${businessId}`, {
      message: data.message,
      conversation_id: data.conversation_id,
      request_id: data.request_id
    })
    .then(response => {
      // Handle successful response
      return response;
    })
    .catch(error => {
      // Handle credit exhaustion or other errors
      if (error.response?.status === 402 || error.response?.data?.status === 'insufficient_credits') {
        // Return the response as a successful response with special status
        return {
          data: {
            response: error.response.data.response,
            status: 'insufficient_credits',
            credits_exhausted: true
          }
        };
      }
      throw error;
    }),

  clearChatHistory: async (businessId) => {
    const response = await apiClient.post(`/businesses/${businessId}/clear-chat`);
    return response.data;
  },

  // Conversation endpoints
  listConversations: (businessId) => 
    apiClient.get(`/businesses/${businessId}/conversations`)
      .then(response => {
        // Map backend fields for sidebar preview
        if (Array.isArray(response.data.conversations)) {
          response.data.conversations = response.data.conversations.map(conv => ({
            ...conv,
            last_user_message: conv.last_user_message || '',
            last_assistant_message: conv.last_assistant_message || '',
            created_at: conv.created_at,
            updated_at: conv.updated_at,
            id: conv.id
          }));
        }
        return response;
      }),
  getConversation: (businessId, conversationId) => 
    apiClient.get(`/businesses/${businessId}/conversations/${conversationId}`)
      .then(response => {
        // Process messages to ensure timestamps
        if (response.data.messages) {
          response.data.messages = response.data.messages.map(msg => {
            // Fix timestamps if missing
            if (!msg.timestamp) {
              const now = new Date();
              
              // Try to create timestamp from the time field if available
              if (msg.time) {
                // Simple conversion from time field to ISO string
                try {
                  const parts = msg.time.match(/(\d+):(\d+)(?::(\d+))?\s*([AP]M)?/i);
                  if (parts) {
                    const [_, hours, minutes, seconds = 0, ampm] = parts;
                    let hour = parseInt(hours);
                    
                    // Convert to 24-hour format if AM/PM is specified
                    if (ampm && ampm.toUpperCase() === 'PM' && hour < 12) hour += 12;
                    if (ampm && ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
                    
                    // Create a new date with just the time parts (use today's date)
                    const date = new Date();
                    date.setHours(hour, parseInt(minutes), seconds ? parseInt(seconds) : 0);
                    
                    msg.timestamp = date.toISOString();
                  } else {
                    // If we can't parse the time format, just use current time
                    msg.timestamp = now.toISOString();
                  }
                } catch (e) {
                  console.warn('Error converting time to timestamp:', e);
                  msg.timestamp = now.toISOString();
                }
              } else {
                // No time field, use current time
                msg.timestamp = now.toISOString();
              }
            }
            
            return msg;
          });
        }
        
        return response;
      }),
  deleteConversation: (businessId, conversationId) => 
    apiClient.delete(`/businesses/${businessId}/conversations/${conversationId}`),
  saveConversation: (businessId, conversationId, data, is_new_message = false) => {
    // Ensure all messages have timestamps before saving
    const updatedData = { ...data };
    
    if (Array.isArray(updatedData.messages)) {
      updatedData.messages = updatedData.messages.map(msg => {
        if (!msg.timestamp) {
          const now = new Date();
          return {
            ...msg,
            timestamp: now.toISOString(),
            time: now.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })
          };
        }
        return msg;
      });
    }
    
    return apiClient.post(`/businesses/${businessId}/conversations/${conversationId}`, {
      ...updatedData,
      is_new_message
    });
  },
  downloadConversation: (businessId, conversationId) => 
    apiClient.get(`/businesses/${businessId}/conversations/${conversationId}`, {
      transformResponse: [(data) => data], // Prevent JSON parsing
      responseType: 'text'
    }),

  createDataSource: async (businessId, data) => {
    try {
      // Check if this is an FAQ submission
      if (data.type === 'faq') {
        // Format FAQ data appropriately for the backend including filename
        const response = await apiClient.post(
          `/businesses/${businessId}/datasources`,
          {
            type: 'faq',
            faqs: data.faqs, // Array of {question, answer} objects
            // We don't need to add filename here as backend will generate it
          }
        );
        return response;
      } else {
        // Extract filename from URL for other data types
        const filename = data.url.split('/').pop() || 'untitled';
        const response = await apiClient.post(
          `/businesses/${businessId}/datasources`,
          {
            ...data,
            filename,  // Add filename field
            documentType: data.type // Ensure documentType is set
          }
        );
        return response;
      }
    } catch (error) {
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Data source creation error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      }
      throw error;
    }
  },

  // Analytics endpoints
  getAnalytics: (businessId, timeRange = '30d') => 
    apiClient.get(`/businesses/${businessId}/analytics`, { 
      params: { timeRange } 
    })
    .catch(error => {
      console.error('Analytics error:', error);
      // If the endpoint is not implemented yet, return mock data
      if (error.response?.status === 404 || error.response?.status === 501) {
        console.log('Returning mock analytics data');
      }
      throw error;
    }),
  testChat,     // Add this new method
  // Add widget settings API methods with debugging
  getWidgetSettings: (businessId) => {
    console.log(`[API] Getting widget settings for business: ${businessId}`);
    return apiClient.get(`/widget/settings/${businessId}`)
      .then(response => {
        console.log('[API] Widget settings response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error getting widget settings:', error);
        throw error;
      });
  },

  updateWidgetSettings: (businessId, settings) => {
    console.log(`[API] Updating widget settings for business: ${businessId}`);
    console.log('[API] Settings payload:', settings);
    
    return apiClient.put(`/widget/settings/${businessId}`, settings)
      .then(response => {
        console.log('[API] Update widget settings response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error updating widget settings:', error);
        console.error('[API] Error response data:', error.response?.data);
        throw error;
      });
  },

  googlePlaces,
  
  // Add website scraping method with extended timeout
  scrapeWebsite: async (data) => {
    try {
      console.log('[API] Starting website scrape request:', data);
      
      // Use extended timeout to match backend processing time (up to 10+ minutes)
      const response = await axios.post(`${API_BASE_URL}/scrape-website`, data, {
        timeout: 660000, // 11 minutes timeout (longer than backend's 10 minute timeout for safety)
        headers: {
          'Content-Type': 'application/json',
        },
        // Add progress tracking
        onUploadProgress: (progressEvent) => {
          console.log('[API] Upload progress:', progressEvent);
        },
        onDownloadProgress: (progressEvent) => {
          console.log('[API] Download progress:', progressEvent);
        }
      });
      
      console.log('[API] Website scrape successful:', response.data);
      return response;
    } catch (error) {
      console.error('[API] Website scrape error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // Handle specific error types with user-friendly messages
      if (error.code === 'ECONNABORTED') {
        error.message = 'Website scraping timed out. The website may be taking too long to process. Please try again with a simpler website or check if the URL is accessible.';
      } else if (error.code === 'ERR_CONNECTION_RESET' || error.code === 'ECONNRESET') {
        error.message = 'Connection was reset during scraping. This usually happens with complex websites. Please try again or try a different website.';
      } else if (error.code === 'ERR_NETWORK') {
        error.message = 'Network error occurred. Please check your internet connection and try again.';
      } else {
        // Use our standardized error message helper
        error.message = api.getScrapingErrorMessage(error);
      }
      
      throw error;
    }
  },

  // Add progress polling method for website scraping
  getScrapingProgress: async (businessId) => {
    try {
      const response = await apiClient.get(`/businesses/${businessId}/scraping-progress`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // No scraping in progress
      }
      throw error;
    }
  },

  // Helper function to get user-friendly error messages
  getScrapingErrorMessage: (error) => {
    const status = error.response?.status;
    
    switch (status) {
      case 429:
        return "We are handling high traffic. Please retry in a few moments.";
      case 500:
        return "Something went wrong on our side. Please try again later.";
      case 502:
      case 503:
        return "Service temporarily unavailable. Please try again later.";
      case 402:
        return "Service temporarily unavailable. Please try again later.";
      default:
        // Check if it's a scraping-specific error from the backend
        if (error.response?.data?.detail) {
          return error.response.data.detail;
        }
        if (error.response?.data?.message) {
          return error.response.data.message;
        }
        return "Oops! Looks like our servers are a bit busy right now. Please try again in a few minutes. Thanks for your patience!";
    }
  },

  // Enhanced scrape website method with progress polling
  scrapeWebsiteWithProgress: async (data, onProgress = null) => {
    try {
      console.log('[API] Starting website scrape with progress tracking:', data);
      
      // Start the scraping process
      const scrapePromise = api.scrapeWebsite(data);
      
      // Poll for progress if callback provided
      if (onProgress && data.business_id) {
        let completed = false;
        
        const pollProgress = async () => {
          try {
            const progress = await api.getScrapingProgress(data.business_id);
            if (progress) {
              console.log('[API] Progress update:', progress);
              onProgress(progress);
              
              // Check if completed successfully
              if (progress.step === 'completed' || progress.progress >= 100) {
                completed = true;
                return; // Stop polling
              }
              
              // Check if there's an error
              if (progress.step === 'error' || progress.error) {
                completed = true;
                console.error('[API] Scraping failed:', progress.message);
                const errorMessage = progress.message || 'Website scraping failed';
                throw new Error(errorMessage);
              }
            }
            
            // Continue polling if not completed and no error
            if (!completed) {
              setTimeout(pollProgress, 1000); // Poll every 1 second for faster feedback
            }
          } catch (error) {
            if (!completed) {
              console.log('[API] Progress polling error, retrying...', error);
              setTimeout(pollProgress, 2000); // Retry with longer delay on error
            } else {
              console.log('[API] Progress polling ended (completed or error)');
            }
          }
        };
        
        // Start polling immediately
        setTimeout(pollProgress, 100);
      }
      
      return await scrapePromise;
    } catch (error) {
      console.error('[API] Website scrape with progress error:', error);
      
      // Handle 504 Gateway Timeout specifically
      if (error.response?.status === 504) {
        console.warn('[API] Scraping timed out but may still be processing in background');
        // Return a partial success for timeouts - let progress polling continue
        return {
          data: {
            success: false,
            timeout: true,
            message: "Website scraping is taking longer than expected. Please check the progress or try again later.",
            step: "timeout"
          }
        };
      }
      
      // Create a user-friendly error message
      const userFriendlyMessage = api.getScrapingErrorMessage(error);
      const enhancedError = new Error(userFriendlyMessage);
      enhancedError.originalError = error;
      enhancedError.status = error.response?.status;
      
      throw enhancedError;
    }
  },

  // Add Google Places indexing method with better error handling
  indexGooglePlaces: async (data) => {
    try {
      const response = await apiClient.post('/google-places-index', data);
      return response;
    } catch (error) {
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Google Places indexing error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      }
      throw error;
    }
  },

  // Add processGooglePlaces method for business setup
  processGooglePlaces: async (businessId, data) => {
    try {
      const response = await apiClient.post('/google-places-index', {
        business_id: businessId,
        place_id: data.place_id,
        name: data.name || '',
        formatted_address: data.formatted_address || '',
        formatted_phone_number: data.formatted_phone_number || '',
        international_phone_number: data.international_phone_number || '',
        website: data.website || '',
        types: data.types || [],
        primary_category: data.primary_category || '',
        geometry: data.geometry || {},
        opening_hours: data.opening_hours || {},
        business_status: data.business_status || '',
        rating: data.rating || null,
        user_ratings_total: data.user_ratings_total || null,
        review_summary: data.review_summary || null
      });
      return response;
    } catch (error) {
      // Extract the specific error message if available
      if (error.response?.data?.detail) {
        console.error(`Google Places processing error: ${error.response.data.detail}`);
        error.message = error.response.data.detail;
      }
      throw error;
    }
  },

  confirmWebsiteContent: async (businessId, data) => {
    return await apiClient.post(`/businesses/${businessId}/website-content/confirm`, data);
  },

  getDocumentContent: async (businessId, documentId) => {
    return await api.get(`/businesses/${businessId}/documents/${documentId}/content`);
  },

  // Add reindex method with proper error handling and logging
  reindexDocument: (businessId, documentId, type = 'google_places') => {
    console.log(`Calling reindex API for business: ${businessId}, document: ${documentId}, type: ${type}`);
    let endpoint;
    if (type === 'google_places') {
      endpoint = `/businesses/${businessId}/documents/${documentId}/reindex-google-place`;
    } else if (type === 'url') {
      endpoint = `/businesses/${businessId}/documents/${documentId}/reindex-website`;
    } else {
      throw new Error('Unsupported document type for reindex');
    }
    // Always POST to this endpoint for both index and re-index
    return axios.post(`${API_BASE_URL}${endpoint}`, {}, {
      timeout: 120000 // 2 minutes for reindexing
    })
    .then(response => {
      console.log('Reindex API success response:', response.data);
      return response;
    })
    .catch(error => {
      console.error('Reindex API error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    });
  },

  // Add logo upload method for widget branding
  uploadWidgetLogo: async (businessId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Use axios directly for file upload
    return axios.post(
      `${API_BASE_URL}/businesses/${businessId}/widget/upload-logo`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      }
    );
  },
  removeWidgetLogo: async (businessId) => {
    return axios.delete(`${API_BASE_URL}/businesses/${businessId}/widget/remove-logo`);
  },
  uploadWidgetLogoWithCrop: async (businessId, formData) => {
    return axios.post(
      `${API_BASE_URL}/businesses/${businessId}/widget/upload-logo`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      }
    );
  },

  // Add business profile fetch method
  getBusinessProfile: (businessId) =>
    apiClient.get(`/business-profile/${businessId}`),
  
  improveMessage: (businessId, conversationId, messageIndex, improvedAnswer, userQuestion = null) =>
  apiClient.post(`/businesses/${businessId}/conversations/${conversationId}/improve`, {
    message_index: messageIndex,
    improved_answer: improvedAnswer,
    user_question: userQuestion
  }),

  // Credit Management APIs
  getCreditStats: (businessId) =>
    apiClient.get(`/businesses/${businessId}/credits/stats`)
      .then(response => response)
      .catch(error => {
        console.error('Error getting credit stats:', error);
        // If server error (500), return default stats
        if (error.response?.status === 500) {
          console.warn('Server error getting credit stats, returning defaults');
          return {
            data: {
              totalQueries: 0,
              totalSpent: 0,
              averageCostPerQuery: 0,
              queriesThisMonth: 0
            }
          };
        }
        throw error;
      }),

  getCreditBalance: (businessId) =>
    apiClient.get(`/businesses/${businessId}/credits`)
      .then(response => response)
      .catch(error => {
        console.error('Error getting credit balance:', error);
        // If server error (500), return default balance
        if (error.response?.status === 500) {
          console.warn('Server error getting credit balance, returning defaults');
          return {
            data: {
              currentCredits: 0,
              totalPurchased: 0,
              totalSpent: 0,
              lastUpdated: new Date().toISOString()
            }
          };
        }
        throw error;
      }),

  initializeCredits: (businessId) =>
    apiClient.post(`/businesses/${businessId}/credits/initialize`)
      .then(response => response)
      .catch(error => {
        console.error('Error initializing credits:', error);
        throw error;
      }),

  getUsageHistory: (businessId, limit = 50) =>
    apiClient.get(`/businesses/${businessId}/credits/usage-history?limit=${limit}`)
      .then(response => response)
      .catch(error => {
        console.error('Error getting usage history:', error);
        // If server error (500), return empty history
        if (error.response?.status === 500) {
          console.warn('Server error getting usage history, returning empty array');
          return {
            data: {
              usage_history: [],
              total_count: 0
            }
          };
        }
        throw error;
      }),

  getPurchaseHistory: (businessId, limit = 50) =>
    apiClient.get(`/businesses/${businessId}/credits/purchase-history?limit=${limit}`)
      .then(response => response)
      .catch(error => {
        console.error('Error getting purchase history:', error);
        // If server error (500), return empty history
        if (error.response?.status === 500) {
          console.warn('Server error getting purchase history, returning empty array');
          return {
            data: {
              purchase_history: [],
              total_count: 0
            }
          };
        }
        throw error;
      }),

  // Stripe Payment (Global)
  createCheckoutSession: (data) =>
    apiClient.post('/create-checkout-session', data)
      .then(response => response)
      .catch(error => {
        console.error('Error creating checkout session:', error);
        throw error;
      }),

  getStripeSessionDetails: (sessionId) =>
    apiClient.get(`/stripe-session-details/${sessionId}`)
      .then(response => response)
      .catch(error => {
        console.error('Error getting Stripe session details:', error);
        throw error;
      }),

  // Razorpay Payment (India)
  createRazorpayOrder: (data) =>
    apiClient.post('/create-razorpay-order', data)
      .then(response => response)
      .catch(error => {
        console.error('Error creating Razorpay order:', error);
        throw error;
      }),

  verifyRazorpayPayment: (data) =>
    apiClient.post('/verify-razorpay-payment', data)
      .then(response => response)
      .catch(error => {
        console.error('Error verifying Razorpay payment:', error);
        throw error;
      }),

  // Check widget availability (credits status)
  checkWidgetAvailability: (businessId) =>
    apiClient.get(`/businesses/${businessId}/credits`)
      .then(response => ({
        data: {
          ...response.data,
          isAvailable: response.data.currentCredits > 0
        }
      }))
      .catch(error => {
        console.error('Error checking widget availability:', error);
        // Return unavailable on error
        return {
          data: {
            currentCredits: 0,
            isAvailable: false
          }
        };
      }),

  // AI Page API methods
  resolveBusinessIdentifier: (businessIdentifier) => {
    console.log(`[API] Resolving business identifier: ${businessIdentifier}`);
    return apiClient.get(`/api/website-chat/resolve/${encodeURIComponent(businessIdentifier)}`)
      .then(response => {
        console.log('[API] Business resolution response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error resolving business identifier:', error);
        throw error;
      });
  },

  getWebsiteChatConfig: (businessId) => {
    console.log(`[API] Getting AI Page config for business: ${businessId}`);
    return apiClient.get(`/api/website-chat/config/${businessId}`)
      .then(response => {
        console.log('[API] AI Page config response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error getting AI Page config:', error);
        throw error;
      });
  },

  updateWebsiteChatConfig: (businessId, config) => {
    console.log(`[API] Updating AI Page config for business: ${businessId}`);
    console.log('[API] Config data:', config);
    return apiClient.post(`/api/website-chat/config/${businessId}`, config)
      .then(response => {
        console.log('[API] Update AI Page config response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error updating AI Page config:', error);
        throw error;
      });
  },

  sendWebsiteChatMessage: (businessId, messageData) => {
    console.log(`[API] Sending AI Page message for business: ${businessId}`);
    return apiClient.post(`/api/website-chat/${businessId}/message`, messageData)
      .then(response => {
        console.log('[API] AI Page message response:', response.data);
        return response;
      })
      .catch(error => {
        console.error('[API] Error sending AI Page message:', error);
        throw error;
      });
  },

  getWebsiteChatConversations: (businessId) => {
    return apiClient.get(`/api/website-chat/${businessId}/conversations`)
      .then(response => response)
      .catch(error => {
        console.error('[API] Error getting AI Page conversations:', error);
        throw error;
      });
  },

  getWebsiteChatStatus: (businessId) => {
    return apiClient.get(`/api/website-chat/${businessId}/status`)
      .then(response => response)
      .catch(error => {
        console.error('[API] Error getting AI Page status:', error);
        throw error;
      });
  },

  // ── Voice AI ────────────────────────────────────────────────────────────

  // updateVoiceConfig — sends raw top-level fields to PUT /businesses/{id}.
  // Distinct from updateBusiness (which wraps data in settings{}).
  updateVoiceConfig: (businessId, data) =>
    apiClient.put(`/businesses/${businessId}`, data).catch(e => { throw e; }),

  getSharedPrompt: (businessId) =>
    apiClient.get('/prompts/shared', { params: businessId ? { uuid: businessId } : {} }),

  updateSharedPrompt: (prompt, businessId) =>
    apiClient.put('/prompts/shared', { prompt, uuid: businessId }),

  playgroundChat: (data) =>
    apiClient.post('/playground/chat', data),

  generateWelcomeMessage: (data) =>
    apiClient.post('/playground/generate-welcome', data),

  generateVoicePrompt: (businessId) =>
    apiClient.post(`/businesses/${businessId}/voice-agent/generate`),

  refreshVoiceSummary: (businessId) =>
    apiClient.post(`/businesses/${businessId}/voice-agent/summary/refresh`),

  saveVoiceSummary: (businessId, summary) =>
    apiClient.put(`/businesses/${businessId}/voice-agent/summary`, summary),

  getVoiceAgentConfig: (businessId) =>
    apiClient.get(`/businesses/${businessId}/voice-agent/config`),

  triggerOutboundCall: (data) =>
    apiClient.post('/trigger/outbound', data),

  getVoiceAnalytics: (businessId) =>
    apiClient.get(`/playground/analytics/${businessId}`),

  // ── Feature 5: Live Inbound Monitor ────────────────────────────────────────

  getLiveInboundCalls: (businessId) =>
    apiClient.get(`/voice/calls/live-inbound`, { params: { business_id: businessId } }),

  whisperToCall: (callLogId, businessId, message) =>
    apiClient.post(`/voice/calls/${callLogId}/whisper`, { message }, { params: { business_id: businessId } }),

  transferCallToOwner: (callLogId, businessId) =>
    apiClient.post(`/voice/calls/${callLogId}/transfer`, {}, { params: { business_id: businessId } }),

  endLiveCall: (callLogId, businessId) =>
    apiClient.post(`/voice/calls/${callLogId}/end`, {}, { params: { business_id: businessId } }),

  // ── Feature 6: Activity Feed ────────────────────────────────────────────────

  // `before` = created_at of the oldest row already loaded (cursor pagination).
  getVoiceCallLogs: (businessId, limit = 20, before = undefined) =>
    apiClient.get(`/voice/call-logs`, {
      params: { business_id: businessId, limit, ...(before ? { before } : {}) },
    }),

  // ── Feature 7: Call Summary Screen ─────────────────────────────────────────

  getVoiceCallLog: (callLogId, businessId) =>
    apiClient.get(`/voice/call-logs/${callLogId}`, { params: { business_id: businessId } }),

  getVoiceSettings: (businessId) =>
    apiClient.get(`/voice/settings/${businessId}`),

  saveVoiceSettings: (businessId, data) =>
    apiClient.put(`/voice/settings/${businessId}`, data),

  getLatestMissedCall: (businessId) =>
    apiClient.get(`/voice/calls/missed/latest`, { params: { business_id: businessId } }),
};


